import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateWorkOrderAssignmentExpense } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderExpenseMutations";
import { Currency, ExpenseType, TaxRegistration } from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { toast } from "sonner";
import { useFormikContext } from "formik";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { addTax, getTaxRate, removeTax } from "@/app/Payments/Utils/taxRates";

const expenseValidation = z.object({
  amount: z.number().min(1, "Amount is required"),
  description: z.string().optional().nullable(),
});

type ExpenseFormValues = z.infer<typeof expenseValidation>;

function getTypeLabel(type: ExpenseType): string {
  switch (type) {
    case ExpenseType.DriverPay:
      return "Driver Pay";
    case ExpenseType.Fuel:
      return "Fuel";
    case ExpenseType.Ferry:
      return "Ferry";
    case ExpenseType.Flight:
      return "Flight";
    case ExpenseType.Accommodation:
      return "Accommodation";
    case ExpenseType.Transportation:
      return "Transportation";
    case ExpenseType.Other:
      return "Other";
    default:
      return "Expense";
  }
}

export function EditExpenseDialog({
  expense,
}: {
  expense: {
    id: string;
    type: ExpenseType;
    amount: number;
    currency: Currency;
    description?: string | null;
    taxRegistration?: TaxRegistration;
  };
}) {
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(updateWorkOrderAssignmentExpense);

  const typeLabel = getTypeLabel(expense.type);
  const isDriverPay = expense.type === ExpenseType.DriverPay;
  const taxRegistration = expense.taxRegistration ?? TaxRegistration.None;
  const taxRate = getTaxRate(taxRegistration);
  const hasTax = isDriverPay && taxRate > 0;
  const excTaxAmount = hasTax
    ? removeTax(expense.amount, taxRegistration)
    : expense.amount;

  return (
    <BaseDialog title={`Edit ${typeLabel}`}>
      <GenericForm<ExpenseFormValues>
        initialValues={{
          amount: isDriverPay ? excTaxAmount : expense.amount,
          description: expense.description ?? "",
        }}
        validationSchema={toFormikValidationSchema(expenseValidation)}
        onSubmit={async (values) => {
          await mutateAsync({
            id: expense.id,
            input: {
              amount: isDriverPay
                ? addTax(values.amount, taxRegistration)
                : values.amount,
              description: values.description,
            },
          });

          toast.success(`${typeLabel} updated successfully`);
          await close();
        }}
      >
        <CurrencyInput
          name="amount"
          label={hasTax ? "Amount (exc. tax)" : "Amount"}
          currency={expense.currency}
          autoFocus
        />
        {hasTax ? (
          <TaxPreview taxRate={taxRate} currency={expense.currency} />
        ) : null}
        {expense.type === ExpenseType.Other ? (
          <TextAreaInput
            name="description"
            label="Description"
            className="col-span-full"
            textAreaProps={{}}
          />
        ) : null}
        <DialogSubmitButton text={`Update ${typeLabel}`} />
      </GenericForm>
    </BaseDialog>
  );
}

function TaxPreview({
  taxRate,
  currency,
}: {
  taxRate: number;
  currency: Currency;
}) {
  const { values } = useFormikContext<ExpenseFormValues>();
  const amount = values.amount ?? 0;
  const tax = Math.round(amount * taxRate);
  const incTax = amount + tax;

  return (
    <p className="col-span-full text-sm text-gray-500">
      Tax: {formatCurrency(tax, currency)} · Total inc. tax:{" "}
      {formatCurrency(incTax, currency)}
    </p>
  );
}
